import { useEffect, useRef, useState } from 'react'

import { Alert } from 'antd'
import { useTranslation } from 'react-i18next'

type AutoLeaveCountdownProps = {
  durationMilliseconds: number
  isActive: boolean
}

export function AutoLeaveCountdown({
  durationMilliseconds,
  isActive,
}: AutoLeaveCountdownProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'page.meeting',
  })
  const countdownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null)

  useEffect(() => {
    const clearCountdownTimer = () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current)
        countdownTimerRef.current = null
      }
    }

    clearCountdownTimer()

    if (!isActive) {
      setRemainingSeconds(null)
      return clearCountdownTimer
    }

    setRemainingSeconds(Math.ceil(durationMilliseconds / 1000))
    countdownTimerRef.current = setInterval(() => {
      setRemainingSeconds((currentRemainingSeconds) => {
        if (currentRemainingSeconds === null) {
          return null
        }

        if (currentRemainingSeconds <= 1) {
          clearCountdownTimer()
          return 0
        }

        return currentRemainingSeconds - 1
      })
    }, 1000)

    return clearCountdownTimer
  }, [durationMilliseconds, isActive])

  if (remainingSeconds === null) {
    return null
  }

  return (
    <Alert
      showIcon
      type="info"
      message={t('autoLeaveCountdown', {
        minutes: Math.floor(remainingSeconds / 60),
        seconds: remainingSeconds % 60,
      })}
      style={{ marginBottom: 16, marginTop: 16 }}
    />
  )
}
